import type {
  ScheduleListResponse,
  DeletedScheduleSummary,
  UserPurchasesResponse,
  UserPurchaseSummaryResponse,
  UserPurchaseSummaryByScheduleResponse,
  VolumeEvolutionPoint,
  TotalPurchasesResponse,
  InterestMetricsResponse,
  PurchaseFilters,
  VolumeGranularity,
} from '@/shared/types/api';

/** Relative /api: in dev Vite proxies to the API; in production the Express server (server/index.ts) proxies /api to the data-api (same pattern as metrics-dashboard). */
const BASE_URL = '/api';

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });

  if (!res.ok) {
    const body = await res.text().catch(() => 'Unknown error');
    throw new ApiError(res.status, `API ${res.status}: ${body}`);
  }

  return res.json() as Promise<T>;
}

function toQueryString(params: Record<string, string | number | boolean | undefined>): string {
  const entries = Object.entries(params).filter(
    (entry): entry is [string, string | number | boolean] => entry[1] !== undefined,
  );
  if (entries.length === 0) return '';
  return '?' + new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString();
}

export const apiClient = {
  schedules: {
    list(params?: { user?: string; token?: string; limit?: number; offset?: number }) {
      const qs = toQueryString(params ?? {});
      return fetchJson<ScheduleListResponse>(`/dca-schedules${qs}`);
    },

    byUserAndToken(user: string, token: string) {
      return fetchJson<{ user: string; token: string; schedules: ScheduleListResponse['schedules']; count: number }>(
        `/dca-schedules/${user}/${token}`,
      );
    },

    deletedList(address: string) {
      return fetchJson<DeletedScheduleSummary[]>(`/users/${address}/deleted-schedules`);
    },
  },

  purchases: {
    byUser(address: string, filters?: PurchaseFilters) {
      const qs = toQueryString({
        page: filters?.page,
        pageSize: filters?.pageSize,
        token: filters?.token,
        startDate: filters?.startDate,
        endDate: filters?.endDate,
        scheduleId: filters?.scheduleId,
      });
      return fetchJson<UserPurchasesResponse>(`/users/${address}/purchases${qs}`);
    },

    summary(address: string, opts?: { scheduleId?: string }) {
      const qs = opts?.scheduleId ? `?scheduleId=${encodeURIComponent(opts.scheduleId)}` : '';
      return fetchJson<UserPurchaseSummaryResponse>(`/users/${address}/purchases/summary${qs}`);
    },

    summaryBySchedule(address: string) {
      return fetchJson<UserPurchaseSummaryByScheduleResponse>(`/users/${address}/purchases/summary-by-schedule`);
    },

    volumeEvolution(params?: {
      granularity?: VolumeGranularity;
      startDate?: number;
      endDate?: number;
    }) {
      const qs = toQueryString(params ?? {});
      return fetchJson<VolumeEvolutionPoint[]>(`/purchase-volume-evolution${qs}`);
    },

    totals() {
      return fetchJson<TotalPurchasesResponse>('/total-purchases');
    },

    byTxHash(txHash: string) {
      return fetchJson<UserPurchasesResponse['purchases'][number]>(`/rbtc-purchases/${txHash}`);
    },
  },

  interest: {
    metrics() {
      return fetchJson<InterestMetricsResponse>('/interest');
    },
  },
} as const;
